import { personRecord } from "./person-record";

/**
 * Whether a hire has told us they cannot switch the macOS grants on.
 *
 * Read only by the walkthrough, so a relaunch -- which macOS asks for after a Screen Recording grant
 * -- does not put the hire back on steps they have already said they cannot finish. The VPN step
 * deliberately reads the live probe instead, so a hire who reported a grant and then switched it on is
 * offered the assistant rather than held to an answer macOS has overtaken.
 */

/**
 * Long enough to cover a Day 1 sitting and the relaunch macOS asks for, short enough not to decide a
 * later onboarding. Within a session the recovery path is the walkthrough itself, which clears this as
 * soon as a probe reports every grant held.
 */
const REFUSAL_TTL_MS = 24 * 60 * 60 * 1000;

const record = personRecord<true>(
  "macGrantsRefused",
  REFUSAL_TTL_MS,
  (value): value is true => value === true
);

/**
 * Record that this hire reported a grant they cannot turn on.
 *
 * @param personId The hire who reported it, or undefined before one is resolved.
 */
export const markGrantsRefused = (personId: string | undefined): void =>
  record.write(personId, true);

/**
 * Clear the refusal, for a hire whose grants have since landed.
 *
 * Called from the walkthrough when macOS reports every grant held, so a later relaunch shows them the
 * checklist they have satisfied rather than the dialog they last dismissed.
 */
export const clearGrantsRefused = (): void => record.clear();

/**
 * Whether this hire reported the grants as unobtainable.
 *
 * @param personId The hire asking, or undefined before one is resolved.
 */
export const hasRefusedGrants = (personId: string | undefined): boolean =>
  record.read(personId) === true;
